import { useState, useEffect } from 'react';
import { TypeAnimation } from 'react-type-animation';
import gojo_bg from '../assets/pfp.webp';

const Hero = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => { if (window.scrollY > 60) setScrolled(true); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleDownloadResume = () => {
    const link = document.createElement('a');
    link.href = '/assets/resume.pdf';
    link.download = 'Abhiram_Gargeya.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleScrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="about"
      className="relative flex flex-col sm:grid sm:grid-cols-2 min-h-[calc(100vh-80px)] items-center gap-12 py-12"
    >
      {/* ── Floating decorative SVG shapes ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Top-left hexagon */}
        <svg
          className="float-shape absolute top-8 left-4 opacity-[0.18]"
          width="90" height="104" viewBox="0 0 90 104"
        >
          <polygon
            points="45,5 85,27 85,77 45,99 5,77 5,27"
            fill="none" stroke="#00f5ff" strokeWidth="1.5"
          />
          <polygon
            points="45,18 73,34 73,70 45,86 17,70 17,34"
            fill="none" stroke="#00f5ff" strokeWidth="0.6"
          />
        </svg>

        {/* Bottom-right diamond */}
        <svg
          className="float-shape-2 absolute bottom-16 right-8 opacity-[0.18]"
          width="70" height="70" viewBox="0 0 70 70"
        >
          <polygon points="35,3 67,35 35,67 3,35" fill="none" stroke="#ff00de" strokeWidth="1.5" />
          <polygon points="35,15 55,35 35,55 15,35" fill="none" stroke="#ff00de" strokeWidth="0.6" />
        </svg>

        {/* Mid crosshair */}
        <svg
          className="float-shape absolute top-1/3 right-[20%] opacity-[0.14]"
          width="44" height="44" viewBox="0 0 44 44"
        >
          <line x1="22" y1="0"  x2="22" y2="44" stroke="#00ff88" strokeWidth="1" />
          <line x1="0"  y1="22" x2="44" y2="22" stroke="#00ff88" strokeWidth="1" />
          <circle cx="22" cy="22" r="4" fill="none" stroke="#00ff88" strokeWidth="1" />
          <circle cx="22" cy="22" r="8" fill="none" stroke="#00ff88" strokeWidth="0.5" />
        </svg>

        {/* Dashed circle */}
        <svg
          className="float-shape-2 absolute bottom-1/3 left-[15%] opacity-[0.12]"
          width="110" height="110" viewBox="0 0 110 110"
        >
          <circle cx="55" cy="55" r="50" fill="none" stroke="#7c3aed" strokeWidth="1" strokeDasharray="6 4" />
          <circle cx="55" cy="55" r="34" fill="none" stroke="#7c3aed" strokeWidth="0.5" strokeDasharray="4 4" />
        </svg>

        {/* Top-right triangle */}
        <svg
          className="float-shape absolute top-20 right-[30%] opacity-[0.10]"
          width="60" height="52" viewBox="0 0 60 52"
        >
          <polygon points="30,3 57,49 3,49" fill="none" stroke="#00f5ff" strokeWidth="1" />
        </svg>
      </div>

      {/* ── Profile image with orbiting rings ── */}
      <div className="w-full flex justify-center reveal-right">
        <div className="relative flex items-center justify-center" style={{ width: 320, height: 320 }}>

          {/* Outer spinning ring */}
          <div
            className="absolute rounded-full"
            style={{
              width:  360, height: 360,
              border: '1.5px solid transparent',
              borderTopColor:   '#00f5ff',
              borderRightColor: '#ff00de',
              borderRadius: '50%',
              animation: 'spin-slow 4s linear infinite',
            }}
          />

          {/* Counter-spin ring */}
          <div
            className="absolute rounded-full"
            style={{
              width:  400, height: 400,
              border: '1px solid transparent',
              borderBottomColor: '#00ff88',
              borderLeftColor:   '#7c3aed',
              borderRadius: '50%',
              animation: 'spin-reverse 7s linear infinite',
            }}
          />

          {/* Orbiting cyan dot */}
          <div
            className="absolute"
            style={{
              width: 360, height: 360,
              borderRadius: '50%',
              animation: 'spin-slow 3s linear infinite',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: -7, left: '50%',
                transform: 'translateX(-50%)',
                width: 14, height: 14,
                borderRadius: '50%',
                background: '#00f5ff',
                boxShadow: '0 0 10px #00f5ff, 0 0 22px #00f5ff',
              }}
            />
          </div>

          {/* Orbiting pink dot */}
          <div
            className="absolute"
            style={{
              width: 360, height: 360,
              borderRadius: '50%',
              animation: 'spin-reverse 5s linear infinite',
            }}
          >
            <div
              style={{
                position: 'absolute',
                bottom: -6, left: '50%',
                transform: 'translateX(-50%)',
                width: 10, height: 10,
                borderRadius: '50%',
                background: '#ff00de',
                boxShadow: '0 0 8px #ff00de, 0 0 18px #ff00de',
              }}
            />
          </div>

          {/* Profile photo */}
          <div
            className="avatar-ring overflow-hidden"
            style={{
              width: 300, height: 300,
              borderRadius: '50%',
              border: '3px solid #00f5ff',
              boxShadow: '0 0 24px rgba(0,245,255,0.45), 0 0 50px rgba(0,245,255,0.2)',
            }}
          >
            <img src={gojo_bg} alt="Abhiram" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>

      {/* ── Text content ── */}
      <div className="flex flex-col items-center sm:items-start justify-center text-center sm:text-left space-y-6 reveal-left">

        {/* Label bar */}
        <div className="flex items-center gap-3">
          <div
            className="h-px w-10"
            style={{ background: '#00f5ff', boxShadow: '0 0 5px #00f5ff' }}
          />
          <span className="text-[#00f5ff] text-xs font-mono tracking-[0.3em] uppercase">
            Software Developer
          </span>
        </div>

        {/* Name */}
        <div>
          <p className="text-slate-400 text-base font-mono mb-1">Hello, World! 👋</p>
          <h1
            className="font-black leading-none"
            style={{
              fontFamily: "'Orbitron', monospace",
              fontSize: 'clamp(3rem, 8vw, 5rem)',
              background: 'linear-gradient(135deg, #ffffff 0%, #00f5ff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            ABHI
          </h1>
          <div className="text-xl sm:text-2xl font-light text-slate-300 mt-2">
            I&apos;m a{' '}
            <TypeAnimation
              style={{
                color: '#00ff88',
                textShadow: '0 0 10px #00ff88, 0 0 20px rgba(0,255,136,0.4)',
              }}
              sequence={[
                'Developer',  2000,
                'Gamer',      1500,
                'Drummer',    1500,
                'Biker',      1500,
                'Athlete',    1500,
              ]}
              wrapper="span"
              speed={50}
              repeat={Infinity}
            />
          </div>
        </div>

        {/* Short bio */}
        <p className="text-slate-400 max-w-md text-sm leading-relaxed">
          Software engineer from Hyderabad, India. I build production-grade
          apps — from real-time web platforms to computer-vision systems.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-row gap-4 flex-wrap justify-center sm:justify-start">
          <button className="neon-btn-solid" onClick={handleDownloadResume}>
            Resume ↓
          </button>
          <button className="neon-btn" onClick={handleScrollToContact}>
            Contact Me
          </button>
        </div>

        {/* Stats */}
        <div className="flex gap-8 pt-2 flex-wrap justify-center sm:justify-start">
          {[
            { value: '9+',  label: 'Projects',    color: '#00f5ff' },
            { value: '2+',  label: 'Experiences', color: '#ff00de' },
            { value: '3+',  label: 'Years Coding', color: '#00ff88' },
          ].map(({ value, label, color }) => (
            <div key={label}>
              <div
                className="text-2xl font-bold"
                style={{ fontFamily: "'Orbitron', monospace", color }}
              >
                {value}
              </div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Scroll hint (fades out after first scroll) ── */}
      <div
        className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 pointer-events-none"
        style={{
          animation: 'float 2.5s ease-in-out infinite',
          opacity: scrolled ? 0 : 0.4,
          transition: 'opacity 0.6s ease',
        }}
      >
        <span className="text-xs font-mono text-[#00f5ff] tracking-widest">SCROLL</span>
        <svg width="16" height="22" viewBox="0 0 16 22" fill="none">
          <rect x="5" y="1" width="6" height="12" rx="3" stroke="#00f5ff" strokeWidth="1.2" />
          <circle cx="8" cy="5" r="1.5" fill="#00f5ff" style={{ animation: 'float 1.5s ease-in-out infinite' }} />
          <polyline points="4,17 8,21 12,17" stroke="#00f5ff" strokeWidth="1.2" fill="none" />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
